import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type {
  V2AgentRef,
  V2MissionStageChangedPayload,
  V2TaskCreatedPayload,
  V2TaskStatusChangedPayload,
} from "@/context/MissionControlWebSocketContext";
import type {
  AgentSummary,
  FaClientDataGathering,
  MissionControlBucketsResponse,
  MissionDetail,
  MissionSummary,
  Page,
  MissionTaskGraphNode,
  MissionTaskSummary,
  TaskActionItem,
  TaskAgentRef,
  TaskBucket,
  ThreadInteraction,
  ThreadMessage,
  ThreadView,
} from "../types";
import * as AgenticV2API from "../index";
import {
  parseThreadArtifactCardContent,
  readArtifactId,
  threadMessageJsonContent,
} from "@/services/api/agentic-v2/threadArtifactContent";
import {
  mergeMissionEntityRefs,
} from "@/components/missions/entityRefs";
import {
  findGraphNodeForTask,
  taskMatchesGraphNode,
} from "@/components/missions/detail/missionTimelineRows";
import { mergeTaskActionItemPatch } from "./wsTaskMerge";
import {
  clientsKeys,
  missionControlKeys,
  type ClientTasksKeyInput,
  type MissionControlBucketsKeyInput,
  type MissionsPageKeyInput,
  type TaskBucketKeyInput,
} from "./keys";
import { taskStatusBelongsInBucket } from "./taskBucketMembership";

function bucketsKey(input: MissionControlBucketsKeyInput) {
  return missionControlKeys.buckets(input);
}

/** Prepend a task row or shallow-merge it when the id already exists in the list. */
function upsertTaskInList(
  items: TaskActionItem[],
  task: TaskActionItem,
  fallbackAgent?: TaskAgentRef | null,
): TaskActionItem[] {
  const idx = items.findIndex((row) => row._id === task._id);
  if (idx === -1) return [task, ...items];
  const next = [...items];
  next[idx] = mergeTaskActionItemPatch(next[idx], task, fallbackAgent);
  return next;
}

/** Drop one task row from a bucket list by id (e.g. after auto-close). */
function removeTaskFromList(items: TaskActionItem[], taskId: string): TaskActionItem[] {
  return items.filter((row) => row._id !== taskId);
}

/** Patch one task row across all loaded bucket slices. */
export function patchTaskRowInBuckets(
  queryClient: QueryClient,
  keyInput: MissionControlBucketsKeyInput,
  task: TaskActionItem,
): void {
  queryClient.setQueryData<MissionControlBucketsResponse>(
    bucketsKey(keyInput),
    (prev) => {
      if (!prev) return prev;
      const buckets = { ...prev.buckets };
      for (const [name, slice] of Object.entries(buckets)) {
        if (!slice) continue;
        buckets[name as TaskBucket] = {
          ...slice,
          items: upsertTaskInList(slice.items, task),
        };
      }
      return { buckets };
    },
  );
}

/** Update status on a matching task row without a full bucket refetch. */
export function patchTaskStatusInBuckets(
  queryClient: QueryClient,
  keyInput: MissionControlBucketsKeyInput,
  payload: { task_id: string; new_status: string },
): void {
  queryClient.setQueryData<MissionControlBucketsResponse>(
    bucketsKey(keyInput),
    (prev) => {
      if (!prev) return prev;
      const buckets = { ...prev.buckets };
      for (const [name, slice] of Object.entries(buckets)) {
        if (!slice) continue;
        buckets[name as TaskBucket] = {
          ...slice,
          items: slice.items.map((row) =>
            row._id === payload.task_id
              ? { ...row, status: payload.new_status }
              : row,
          ),
        };
      }
      return { buckets };
    },
  );
}

export type TaskBucketPageInfiniteData = InfiniteData<Page<TaskActionItem>>;

const TASK_BUCKET_QUERY_ROOT = [...missionControlKeys.all, "task-bucket"] as const;

function isTaskBucketKeyInput(value: unknown): value is TaskBucketKeyInput {
  if (typeof value !== "object" || value === null) return false;
  const row = value as TaskBucketKeyInput;
  return typeof row.bucket === "string" && typeof row.visibility === "string";
}

/** Filtered lists (search / mission) only update rows they already hold — the server decides membership. */
function canInsertIntoTaskBucket(keyInput: TaskBucketKeyInput, task: TaskActionItem): boolean {
  if (keyInput.q) return false;
  return !keyInput.missionId || task.mission?.id === keyInput.missionId;
}

function patchTaskInTaskBucketInfiniteData(
  data: TaskBucketPageInfiniteData | undefined,
  keyInput: TaskBucketKeyInput,
  task: TaskActionItem,
): TaskBucketPageInfiniteData | undefined {
  if (!data?.pages.length) return data;
  const belongs = taskStatusBelongsInBucket(task.status, keyInput.bucket);
  if (!belongs) {
    return {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: removeTaskFromList(page.items, task._id),
      })),
    };
  }
  const foundPage = data.pages.findIndex((page) =>
    page.items.some((row) => row._id === task._id),
  );
  if (foundPage === -1) {
    if (!canInsertIntoTaskBucket(keyInput, task)) return data;
    const pages = [...data.pages];
    pages[0] = {
      ...pages[0],
      items: upsertTaskInList(pages[0].items, task),
    };
    return { ...data, pages };
  }
  return {
    ...data,
    pages: data.pages.map((page, index) => {
      if (index === foundPage) {
        return { ...page, items: upsertTaskInList(page.items, task) };
      }
      return { ...page, items: removeTaskFromList(page.items, task._id) };
    }),
  };
}

function patchTaskStatusInTaskBucketInfiniteData(
  data: TaskBucketPageInfiniteData | undefined,
  bucket: TaskBucket,
  payload: { task_id: string; new_status: string },
): TaskBucketPageInfiniteData | undefined {
  if (!data?.pages.length) return data;
  const belongs = taskStatusBelongsInBucket(payload.new_status, bucket);
  const pages = data.pages.map((page) => {
    if (!belongs) {
      return { ...page, items: removeTaskFromList(page.items, payload.task_id) };
    }
    const idx = page.items.findIndex((row) => row._id === payload.task_id);
    if (idx === -1) return page;
    const items = [...page.items];
    items[idx] = { ...items[idx], status: payload.new_status };
    return { ...page, items };
  });
  return { ...data, pages };
}

/** Patch one task row across cached Mission Control task-bucket infinite lists. */
export function patchTaskRowInTaskBucketCaches(
  queryClient: QueryClient,
  task: TaskActionItem,
): void {
  const queries = queryClient.getQueriesData<TaskBucketPageInfiniteData>({
    queryKey: TASK_BUCKET_QUERY_ROOT,
  });
  for (const [queryKey, data] of queries) {
    if (!Array.isArray(queryKey) || queryKey.length < 3) continue;
    const keyInput = queryKey[2];
    if (!isTaskBucketKeyInput(keyInput)) continue;
    const next = patchTaskInTaskBucketInfiniteData(data, keyInput, task);
    if (next !== data) {
      queryClient.setQueryData(queryKey, next);
    }
  }
}

/** Update status on a matching row in cached task-bucket lists; drop when it leaves the bucket. */
export function patchTaskStatusInTaskBucketCaches(
  queryClient: QueryClient,
  payload: { task_id: string; new_status: string; task?: TaskActionItem | null },
): void {
  if (payload.task) {
    patchTaskRowInTaskBucketCaches(queryClient, {
      ...payload.task,
      _id: payload.task_id,
      status: payload.new_status,
    });
    return;
  }

  const queries = queryClient.getQueriesData<TaskBucketPageInfiniteData>({
    queryKey: TASK_BUCKET_QUERY_ROOT,
  });
  for (const [queryKey, data] of queries) {
    if (!Array.isArray(queryKey) || queryKey.length < 3) continue;
    const keyInput = queryKey[2];
    if (!isTaskBucketKeyInput(keyInput)) continue;
    const next = patchTaskStatusInTaskBucketInfiniteData(
      data,
      keyInput.bucket,
      payload,
    );
    if (next !== data) {
      queryClient.setQueryData(queryKey, next);
    }
  }
}

/** Refetch count-only bucket snapshots (Mission Control tab badges). */
export function invalidateMissionControlBucketCounts(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === missionControlKeys.all[0] &&
        key[1] === "buckets" &&
        key.includes("counts-only")
      );
    },
  });
}

/** Refetch bucket snapshot after a task moves between buckets or is created/deleted. */
export function invalidateMissionControlBuckets(
  queryClient: QueryClient,
  keyInput: MissionControlBucketsKeyInput,
): void {
  void queryClient.invalidateQueries({ queryKey: bucketsKey(keyInput) });
  invalidateMissionControlBucketCounts(queryClient);
}

function mergeInteractions(
  existing: ThreadInteraction[],
  incoming: ThreadInteraction[],
): ThreadInteraction[] {
  const byId = new Map(existing.map((row) => [row._id, row]));
  for (const row of incoming) {
    const prev = byId.get(row._id);
    if (!prev) {
      byId.set(row._id, row);
      continue;
    }
    const steps =
      row.steps.length >= prev.steps.length ? row.steps : [...prev.steps, ...row.steps];
    byId.set(row._id, { ...prev, ...row, steps });
  }
  return [...byId.values()].sort(
    (a, b) => a.sequence_number - b.sequence_number,
  );
}

function mergeMessages(
  existing: ThreadMessage[],
  incoming: ThreadMessage[],
): ThreadMessage[] {
  const seen = new Set(existing.map((row) => row._id));
  const merged = [...existing];
  for (const row of incoming) {
    if (seen.has(row._id)) continue;
    seen.add(row._id);
    merged.push(row);
  }
  merged.sort((a, b) => a.created_at.localeCompare(b.created_at));
  return merged;
}

/** Merge a delta ``ThreadView`` into the cached full thread. */
export function mergeThreadViews(
  base: ThreadView,
  delta: ThreadView,
): ThreadView {
  if (!delta.is_delta) return delta;
  return {
    ...base,
    messages: mergeMessages(base.messages, delta.messages),
    interactions: mergeInteractions(base.interactions, delta.interactions),
    cursor: delta.cursor ?? base.cursor,
    is_delta: false,
  };
}

/** Append one message from a WS progress payload. */
export function patchThreadMessage(
  queryClient: QueryClient,
  taskId: string,
  message: ThreadMessage,
): void {
  queryClient.setQueryData<ThreadView>(
    missionControlKeys.taskThread(taskId),
    (prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        messages: mergeMessages(prev.messages, [message]),
        cursor: prev.cursor,
      };
    },
  );
}

/** Fetch and inline an artefact on a thread ``artifact_card`` message.

Fails silently when the artefact cannot be loaded — the thread row still
renders and ``ThreadArtifactCard`` retries via its own query.
*/
export async function hydrateArtifactCardMessage(
  queryClient: QueryClient,
  taskId: string,
  message: ThreadMessage,
): Promise<void> {
  if (message.content_type !== "artifact_card") return;
  const content = threadMessageJsonContent(message.content);
  if (!content) return;
  const { artifact: existing } = parseThreadArtifactCardContent(content);
  if (existing) return;
  const artifactId = readArtifactId(content);
  if (!artifactId) return;

  try {
    const artifact = await AgenticV2API.getArtifact(artifactId);
    const hydrated: ThreadMessage = {
      ...message,
      content: {
        ...content,
        artifact,
      },
    };

    queryClient.setQueryData<ThreadView>(
      missionControlKeys.taskThread(taskId),
      (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((row) =>
            row._id === message._id ? hydrated : row,
          ),
          cursor: prev.cursor,
        };
      },
    );
  } catch {
    // ThreadArtifactCard falls back to its own fetch when inline hydration fails.
  }
}

/** Append or replace one step on a running interaction. */
export function patchThreadStep(
  queryClient: QueryClient,
  taskId: string,
  payload: {
    interaction_id: string;
    step_index: number;
    step: ThreadInteraction["steps"][number];
  },
): void {
  queryClient.setQueryData<ThreadView>(
    missionControlKeys.taskThread(taskId),
    (prev) => {
      if (!prev) return prev;
      const interactions = prev.interactions.map((interaction) => {
        if (interaction._id !== payload.interaction_id) return interaction;
        const steps = [...interaction.steps];
        steps[payload.step_index] = payload.step;
        return { ...interaction, steps };
      });
      return { ...prev, interactions };
    },
  );
}

// --- Missions browser cache (see useMissionsPage / MissionsBrowser) ---

function missionsPageKey(input: MissionsPageKeyInput) {
  return missionControlKeys.missionsPage(input);
}

function upsertMissionInList(
  items: MissionSummary[],
  mission: MissionSummary,
): MissionSummary[] {
  const idx = items.findIndex((row) => row._id === mission._id);
  if (idx === -1) return [mission, ...items];
  const next = [...items];
  const existing = next[idx];
  next[idx] = {
    ...existing,
    ...mission,
    entities: mergeMissionEntityRefs(existing.entities, mission.entities),
  };
  return next;
}

export type MissionsPageInfiniteData = InfiniteData<Page<MissionSummary>>;

/** Patch a mission row in place, or prepend it to the first page when not loaded yet. */
export function patchMissionInList(
  queryClient: QueryClient,
  keyInput: MissionsPageKeyInput,
  mission: MissionSummary,
): void {
  queryClient.setQueryData<MissionsPageInfiniteData>(missionsPageKey(keyInput), (prev) => {
    if (!prev?.pages.length) return prev;
    const found = prev.pages.findIndex((page) => page.items.some((row) => row._id === mission._id));
    const target = found === -1 ? 0 : found;
    const pages = prev.pages.map((page, i) =>
      i === target ? { ...page, items: upsertMissionInList(page.items, mission) } : page,
    );
    return { ...prev, pages };
  });
}

/** Merge partial mission fields onto an existing cached row. */
export function patchMissionFieldsInList(
  queryClient: QueryClient,
  keyInput: MissionsPageKeyInput,
  missionId: string,
  patch: Partial<MissionSummary>,
): void {
  queryClient.setQueryData<MissionsPageInfiniteData>(missionsPageKey(keyInput), (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      pages: prev.pages.map((page) => ({
        ...page,
        items: page.items.map((row) =>
          row._id === missionId ? { ...row, ...patch } : row,
        ),
      })),
    };
  });
}

export { removeTaskFromList, upsertTaskInList };

// --- Clients app caches (see useClient* hooks / ClientDetail) ---

function clientTasksKey(input: ClientTasksKeyInput) {
  return clientsKeys.tasks(input);
}

/** True when a mission summary references the given fa_client entity. */
export function missionBelongsToFaClient(
  mission: MissionSummary,
  faClientId: string,
): boolean {
  return (
    mission.entities?.some(
      (entity) =>
        (entity.entity_type === "fa_client" || entity.entity_type === "client") &&
        entity.entity_id === faClientId,
    ) ?? false
  );
}

/** Entity ref on WS task/mission payloads (subset used for client scoping). */
export type TaskScopeEntityRef = {
  entity_type?: string | null;
  entity_id?: string | null;
};

export type TaskScopePayload = {
  task_id?: string | null;
  entity?: TaskScopeEntityRef | null;
  mission_id?: string | null;
};

function entityMatchesFaClient(
  entity: TaskScopeEntityRef | null | undefined,
  faClientId: string,
): boolean {
  if (!entity?.entity_id) return false;
  const type = entity.entity_type ?? "";
  return (
    entity.entity_id === faClientId &&
    (type === "fa_client" || type === "client" || type === "")
  );
}

function isClientTasksKeyInput(value: object): value is ClientTasksKeyInput {
  return (
    "faClientId" in value &&
    "visibility" in value &&
    typeof (value as ClientTasksKeyInput).faClientId === "string"
  );
}

/** Match WS events to a client using entity ref and cached mission membership. */
export function taskBelongsToFaClientScope(
  queryClient: QueryClient,
  faClientId: string,
  payload: TaskScopePayload,
): boolean {
  const dgSummary = queryClient
    .getQueriesData<FaClientDataGathering>({
      queryKey: [...clientsKeys.all, "data-gathering", faClientId],
    })
    .find(([, value]) => value != null)?.[1];
  if (
    payload.task_id &&
    dgSummary?.dg_monitoring_task_id &&
    payload.task_id === dgSummary.dg_monitoring_task_id
  ) {
    return true;
  }

  if (entityMatchesFaClient(payload.entity, faClientId)) return true;

  const missionId = payload.mission_id;
  if (!missionId) return false;

  const missions = queryClient
    .getQueriesData<MissionSummary[]>({
      queryKey: [...clientsKeys.all, "missions", faClientId],
    })
    .find(([, value]) => value != null)?.[1];
  if (missions?.some((mission) => mission._id === missionId)) return true;

  const clientTaskQueries = queryClient.getQueriesData<TaskActionItem[]>({
    queryKey: [...clientsKeys.all, "tasks"],
  });
  for (const [queryKey, items] of clientTaskQueries) {
    if (!Array.isArray(queryKey) || queryKey.length < 3) continue;
    const keyInput = queryKey[2];
    if (typeof keyInput !== "object" || keyInput === null || !isClientTasksKeyInput(keyInput)) {
      continue;
    }
    if (keyInput.faClientId !== faClientId) continue;
    if (items?.some((task) => task.mission?.id === missionId)) return true;
  }

  const detail = queryClient.getQueryData<MissionDetailBundle>(
    missionControlKeys.missionDetail(missionId),
  );
  return (
    detail?.mission.entities.some(
      (entity) =>
        (entity.entity_type === "fa_client" || entity.entity_type === "client") &&
        entity.entity_id === faClientId,
    ) ?? false
  );
}

/** Patch or prepend one task row in a cached client-scoped task list. */
export function patchTaskInClientList(
  queryClient: QueryClient,
  keyInput: ClientTasksKeyInput,
  task: TaskActionItem,
  fallbackAgent?: TaskAgentRef | null,
): void {
  queryClient.setQueryData<TaskActionItem[]>(clientTasksKey(keyInput), (prev) => {
    if (!prev) return [task];
    return upsertTaskInList(prev, task, fallbackAgent);
  });
}

/** Update status on a matching row in a cached client-scoped task list. */
export function patchTaskStatusInClientList(
  queryClient: QueryClient,
  keyInput: ClientTasksKeyInput,
  payload: { task_id: string; new_status: string },
): void {
  queryClient.setQueryData<TaskActionItem[]>(clientTasksKey(keyInput), (prev) => {
    if (!prev) return prev;
    return prev.map((row) =>
      row._id === payload.task_id
        ? { ...row, status: payload.new_status }
        : row,
    );
  });
}

/** Scoped invalidation when WS payloads lack enough data to patch client tasks. */
export function invalidateClientTasks(
  queryClient: QueryClient,
  keyInput: ClientTasksKeyInput,
): void {
  void queryClient.invalidateQueries({ queryKey: clientTasksKey(keyInput) });
}

/** Patch or prepend one mission row in a cached client missions list. */
export function patchMissionInClientList(
  queryClient: QueryClient,
  faClientId: string,
  mission: MissionSummary,
): void {
  if (!missionBelongsToFaClient(mission, faClientId)) return;
  queryClient.setQueriesData<MissionSummary[]>(
    { queryKey: [...clientsKeys.all, "missions", faClientId] },
    (prev) => {
      if (!prev) return [mission];
      return upsertMissionInList(prev, mission);
    },
  );
}

/** Merge partial mission fields onto an existing cached client missions row. */
export function patchMissionFieldsInClientList(
  queryClient: QueryClient,
  faClientId: string,
  missionId: string,
  patch: Partial<MissionSummary>,
): void {
  queryClient.setQueriesData<MissionSummary[]>(
    { queryKey: [...clientsKeys.all, "missions", faClientId] },
    (prev) => {
      if (!prev) return prev;
      return prev.map((row) =>
        row._id === missionId ? { ...row, ...patch } : row,
      );
    },
  );
}

/** Scoped invalidation for client missions when WS payloads lack hydrated rows. */
export function invalidateClientMissions(
  queryClient: QueryClient,
  faClientId: string,
): void {
  void queryClient.invalidateQueries({
    queryKey: [...clientsKeys.all, "missions", faClientId],
  });
}

// --- Mission detail cache (see useMissionDetail / MissionDetail) ---

export interface MissionDetailBundle {
  mission: MissionDetail;
  /** Seeded agents list from the initial mission-detail fetch; must survive WS patches. */
  agents?: AgentSummary[];
}

function missionDetailKey(missionId: string) {
  return missionControlKeys.missionDetail(missionId);
}

function withPatchedMission(
  prev: MissionDetailBundle,
  mission: MissionDetail,
): MissionDetailBundle {
  return { ...prev, mission };
}

/** Merge partial mission fields onto the cached mission detail document. */
export function patchMissionDetailFields(
  queryClient: QueryClient,
  missionId: string,
  patch: Partial<MissionDetail>,
): void {
  queryClient.setQueryData<MissionDetailBundle>(missionDetailKey(missionId), (prev) => {
    if (!prev) return prev;
    const entities =
      patch.entities !== undefined
        ? mergeMissionEntityRefs(prev.mission.entities, patch.entities)
        : prev.mission.entities;
    return withPatchedMission(prev, { ...prev.mission, ...patch, entities });
  });
}

/** Merge summary fields from a WS mission row onto cached mission detail. */
export function patchMissionDetailFromSummary(
  queryClient: QueryClient,
  missionId: string,
  summary: MissionSummary,
): void {
  patchMissionDetailFields(queryClient, missionId, summary);
}

function upsertMissionTask(
  tasks: MissionTaskSummary[],
  task: MissionTaskSummary,
  graph: MissionTaskGraphNode[] = [],
): MissionTaskSummary[] {
  const idx = tasks.findIndex((row) => row._id === task._id);
  if (idx !== -1) {
    const next = [...tasks];
    next[idx] = mergeMissionTaskSummary(next[idx], task);
    return next;
  }

  const node = findGraphNodeForTask(task, graph);
  if (node) {
    const slotIdx = tasks.findIndex((row) => taskMatchesGraphNode(row, node));
    if (slotIdx !== -1) {
      const next = [...tasks];
      next[slotIdx] = mergeMissionTaskSummary(next[slotIdx], task);
      return next;
    }
  }

  return [...tasks, task];
}

/** Preserve graph identity fields when WS patches omit them. */
export function mergeMissionTaskSummary(
  existing: MissionTaskSummary,
  incoming: MissionTaskSummary,
): MissionTaskSummary {
  return {
    ...existing,
    ...incoming,
    stage:
      incoming.stage !== undefined && incoming.stage !== null
        ? incoming.stage
        : existing.stage,
    spawned_from_node:
      incoming.spawned_from_node !== undefined && incoming.spawned_from_node !== null
        ? incoming.spawned_from_node
        : existing.spawned_from_node,
    catalogue_id: incoming.catalogue_id?.trim()
      ? incoming.catalogue_id
      : existing.catalogue_id,
    latest_summary:
      incoming.latest_summary !== undefined && incoming.latest_summary !== null
        ? incoming.latest_summary
        : existing.latest_summary,
    assigned_agent_id:
      incoming.assigned_agent_id != null && incoming.assigned_agent_id !== ""
        ? incoming.assigned_agent_id
        : existing.assigned_agent_id,
  };
}

/** Upsert one task row in the cached mission detail timeline. */
export function patchTaskInMissionDetail(
  queryClient: QueryClient,
  missionId: string,
  task: MissionTaskSummary,
): void {
  queryClient.setQueryData<MissionDetailBundle>(missionDetailKey(missionId), (prev) => {
    if (!prev) return prev;
    return withPatchedMission(prev, {
      ...prev.mission,
      tasks: upsertMissionTask(prev.mission.tasks, task, prev.mission.task_graph ?? []),
    });
  });
}

/** Patch status (and optional fields) on one mission task by id. */
export function patchMissionTaskFields(
  queryClient: QueryClient,
  missionId: string,
  taskId: string,
  patch: Partial<MissionTaskSummary>,
): void {
  queryClient.setQueryData<MissionDetailBundle>(missionDetailKey(missionId), (prev) => {
    if (!prev) return prev;
    return withPatchedMission(prev, {
      ...prev.mission,
      tasks: prev.mission.tasks.map((row) =>
        row._id === taskId ? mergeMissionTaskSummary(row, { ...row, ...patch }) : row,
      ),
    });
  });
}

/** Scoped invalidation when WS payloads lack enough data to patch. */
export function invalidateMissionDetail(
  queryClient: QueryClient,
  missionId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: missionDetailKey(missionId) });
  void queryClient.invalidateQueries({
    queryKey: missionControlKeys.missionContext(missionId),
  });
}

/** Fields supplied by WS envelopes when mapping a hydrated task row to mission detail. */
export type MissionTaskFromWsFallback = {
  task_id: string;
  name: string;
  status: string;
  task_type: string;
  stage?: string | null;
  catalogue_id?: string | null;
  assigned_agent_id?: string | null;
  spawned_from_node?: string | null;
};

/** Map a hydrated WS task row onto mission timeline task fields where possible. */
export function missionTaskFromWsRow(
  task: TaskActionItem,
  fallback: MissionTaskFromWsFallback,
): MissionTaskSummary {
  const catalogueFromTask = task.catalogue_id?.trim();
  const catalogueFromFallback = fallback.catalogue_id?.trim();
  const row: MissionTaskSummary = {
    _id: task._id ?? fallback.task_id,
    name: fallback.name ?? task.name ?? "",
    task_type: task.task_type ?? fallback.task_type,
    status: fallback.status ?? task.status ?? "",
    assigned_agent_id: task.agent?.id ?? fallback.assigned_agent_id ?? null,
    catalogue_id: catalogueFromTask || catalogueFromFallback || "",
    queued_at: task.queued_at ?? null,
    started_at: task.started_at ?? null,
    completed_at: task.completed_at ?? null,
    updated_at: task.updated_at ?? new Date().toISOString(),
    gate_blocked_reason: task.gate_blocked_reason ?? null,
    gate_blocked_display: task.gate_blocked_display ?? null,
    latest_summary: task.latest_summary ?? null,
  };
  const stage = task.stage ?? fallback.stage;
  if (stage !== undefined) {
    row.stage = stage;
  }
  const spawnedFromNode = task.spawned_from_node ?? fallback.spawned_from_node;
  if (spawnedFromNode !== undefined) {
    row.spawned_from_node = spawnedFromNode;
  }
  return row;
}

/** Seed a minimal agent stub into the cached agents list when a WS payload references an agent not yet loaded. */
function upsertAgentStub(
  queryClient: QueryClient,
  missionId: string,
  agent: V2AgentRef,
): void {
  queryClient.setQueryData<MissionDetailBundle>(missionDetailKey(missionId), (prev) => {
    if (!prev) return prev;
    const agents = prev.agents ?? [];
    if (agents.some((row) => row._id === agent.id)) return prev;
    const stub: AgentSummary = {
      _id: agent.id,
      name: agent.name,
      role: agent.role ?? "",
      description: "",
      avatar_colour: null,
      status: "active",
      tags: [],
    };
    return { ...prev, agents: [...agents, stub] };
  });
}

function missionDetailCacheLoaded(
  queryClient: QueryClient,
  missionId: string,
): boolean {
  return queryClient.getQueryData<MissionDetailBundle>(missionDetailKey(missionId)) != null;
}

/** Advance stage rail state on cached mission detail when ``current_stage`` moves. */
export function patchMissionDetailStageTransition(
  queryClient: QueryClient,
  missionId: string,
  transition: Pick<V2MissionStageChangedPayload, "new_stage" | "old_stage">,
): void {
  queryClient.setQueryData<MissionDetailBundle>(missionDetailKey(missionId), (prev) => {
    if (!prev) return prev;
    const now = new Date().toISOString();
    const { new_stage, old_stage } = transition;
    const stages = prev.mission.stages.map((stage) => {
      if (old_stage && stage.name === old_stage) {
        return {
          ...stage,
          status: "completed",
          completed_at: stage.completed_at ?? now,
        };
      }
      if (stage.name === new_stage) {
        return {
          ...stage,
          status: "active",
          entered_at: stage.entered_at ?? now,
          completed_at: null,
        };
      }
      if (stage.status === "active" && stage.name !== new_stage) {
        return {
          ...stage,
          status: "completed",
          completed_at: stage.completed_at ?? now,
        };
      }
      return stage;
    });
    const activeStage = stages.find((stage) => stage.name === new_stage);
    return withPatchedMission(prev, {
      ...prev.mission,
      current_stage: new_stage,
      current_stage_display:
        activeStage?.display_name ?? prev.mission.current_stage_display,
      stages,
      updated_at: now,
    });
  });
}

/** Apply ``v2.mission.stage_changed`` to mission detail when that cache is loaded. */
export function applyMissionStageChangedToMissionDetailCache(
  queryClient: QueryClient,
  payload: V2MissionStageChangedPayload,
): void {
  const missionId = payload.mission_id;
  if (!missionId || !missionDetailCacheLoaded(queryClient, missionId)) return;

  if (payload.mission) {
    patchMissionDetailFromSummary(queryClient, missionId, payload.mission);
  } else {
    patchMissionDetailFields(queryClient, missionId, {
      current_stage: payload.new_stage,
    });
  }
  patchMissionDetailStageTransition(queryClient, missionId, payload);
}

/** Apply ``v2.task.created`` to mission detail when that cache is loaded. */
export function applyTaskCreatedToMissionDetailCache(
  queryClient: QueryClient,
  payload: V2TaskCreatedPayload,
): void {
  const missionId = payload.mission_id;
  if (!missionId) return;
  if (!missionDetailCacheLoaded(queryClient, missionId)) {
    void queryClient.invalidateQueries({ queryKey: missionDetailKey(missionId) });
    return;
  }

  if (payload.task) {
    patchTaskInMissionDetail(
      queryClient,
      missionId,
      missionTaskFromWsRow(payload.task, {
        task_id: payload.task_id,
        name: payload.name,
        status: payload.status,
        task_type: payload.task_type,
        stage: payload.stage ?? null,
        catalogue_id: payload.catalogue_id,
        assigned_agent_id: payload.assigned_agent_id,
        spawned_from_node: payload.spawned_from_node,
      }),
    );
    return;
  }

  patchTaskInMissionDetail(queryClient, missionId, {
    _id: payload.task_id,
    name: payload.name,
    task_type: payload.task_type,
    status: payload.status,
    assigned_agent_id: payload.assigned_agent_id,
    catalogue_id: payload.catalogue_id ?? "",
    stage: payload.stage ?? null,
    spawned_from_node: payload.spawned_from_node,
    queued_at: null,
    started_at: null,
    completed_at: null,
    updated_at: new Date().toISOString(),
  });
}

/** Apply ``v2.task.status_changed`` to mission detail when that cache is loaded. */
export function applyTaskStatusChangedToMissionDetailCache(
  queryClient: QueryClient,
  payload: V2TaskStatusChangedPayload,
): void {
  const missionId = payload.mission_id;
  if (!missionId || !missionDetailCacheLoaded(queryClient, missionId)) return;

  if (payload.agent) {
    upsertAgentStub(queryClient, missionId, payload.agent);
  }

  if (payload.task) {
    const fallback: MissionTaskFromWsFallback = {
      task_id: payload.task_id,
      name: payload.name,
      status: payload.new_status,
      task_type: payload.task.task_type,
    };
    if (payload.stage !== undefined) {
      fallback.stage = payload.stage;
    }
    if (payload.spawned_from_node !== undefined) {
      fallback.spawned_from_node = payload.spawned_from_node;
    }
    if (payload.task.spawned_from_node !== undefined) {
      fallback.spawned_from_node = payload.task.spawned_from_node;
    }
    if (payload.task.catalogue_id) {
      fallback.catalogue_id = payload.task.catalogue_id;
    }
    if (payload.task.stage !== undefined) {
      fallback.stage = payload.task.stage;
    }
    if (payload.agent?.id) {
      fallback.assigned_agent_id = payload.agent.id;
    }
    patchTaskInMissionDetail(
      queryClient,
      missionId,
      missionTaskFromWsRow(payload.task, fallback),
    );
    return;
  }

  patchMissionTaskFields(queryClient, missionId, payload.task_id, {
    status: payload.new_status,
    name: payload.name,
    assigned_agent_id: payload.agent?.id ?? undefined,
  });
}
